import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { HelpCircle, X } from 'lucide-react';

const BREITE = 320;
const ABSTAND = 8;

/** Fragezeichen mit anklickbarer Erklärung. Die Blase sucht sich ihren Platz selbst:
 *  bevorzugt unter dem Zeichen, sonst darüber, sonst daneben — je nachdem, wo noch
 *  Platz im Fenster ist. Ein Tooltip am title-Attribut reichte nicht: er verschwindet
 *  beim Wegbewegen der Maus und ist auf Touch-Geräten gar nicht erreichbar.
 *
 *  Der Auslöser ist bewusst ein <span role="button"> und kein <button> — er steht in
 *  Zeilen, die selbst Knöpfe sind, und verschachtelte Buttons sind ungültig. */
export function HinweisPopover({ text, label = 'Erklärung anzeigen', titel }: { text: string; label?: string; titel?: string }) {
  const [offen, setOffen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const ausloeserRef = useRef<HTMLSpanElement>(null);
  const blaseRef = useRef<HTMLDivElement>(null);

  // Nach dem Öffnen die tatsächliche Höhe messen und die Blase platzieren.
  useLayoutEffect(() => {
    if (!offen) { setPos(null); return; }
    const a = ausloeserRef.current?.getBoundingClientRect();
    const h = blaseRef.current?.offsetHeight ?? 0;
    if (!a) return;
    const fensterH = window.innerHeight;
    const fensterB = window.innerWidth;

    let top: number;
    if (a.bottom + ABSTAND + h <= fensterH - ABSTAND) {
      top = a.bottom + ABSTAND;                       // darunter
    } else if (a.top - ABSTAND - h >= ABSTAND) {
      top = a.top - ABSTAND - h;                      // darüber
    } else {
      top = Math.min(Math.max(ABSTAND, a.top + a.height / 2 - h / 2), fensterH - h - ABSTAND);
    }

    // Waagerecht mittig unter dem Zeichen, aber nie über den Fensterrand hinaus.
    // Reicht es weder oben noch unten, rückt die Blase seitlich neben das Zeichen.
    const seitlich = top !== a.bottom + ABSTAND && top !== a.top - ABSTAND - h;
    let left = seitlich
      ? (a.right + ABSTAND + BREITE <= fensterB - ABSTAND ? a.right + ABSTAND : a.left - ABSTAND - BREITE)
      : a.left + a.width / 2 - BREITE / 2;
    left = Math.min(Math.max(ABSTAND, left), fensterB - BREITE - ABSTAND);

    setPos({ top, left });
  }, [offen]);

  // Schließen bei Klick daneben, Escape, Scrollen oder Größenänderung — die Blase
  // liegt fix im Fenster und würde sonst neben ihrem Zeichen stehen bleiben.
  useEffect(() => {
    if (!offen) return;
    const zu = (e: MouseEvent) => {
      if (blaseRef.current?.contains(e.target as Node)) return;
      if (ausloeserRef.current?.contains(e.target as Node)) return;
      setOffen(false);
    };
    const taste = (e: KeyboardEvent) => { if (e.key === 'Escape') setOffen(false); };
    const weg = () => setOffen(false);
    document.addEventListener('mousedown', zu);
    document.addEventListener('keydown', taste);
    window.addEventListener('scroll', weg, true);
    window.addEventListener('resize', weg);
    return () => {
      document.removeEventListener('mousedown', zu);
      document.removeEventListener('keydown', taste);
      window.removeEventListener('scroll', weg, true);
      window.removeEventListener('resize', weg);
    };
  }, [offen]);

  const umschalten = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setOffen(o => !o);
  };

  return (
    <>
      <span
        ref={ausloeserRef}
        role="button"
        tabIndex={0}
        aria-label={label}
        aria-expanded={offen}
        onClick={umschalten}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') umschalten(e); }}
        className={`shrink-0 rounded-full transition-colors ${offen ? 'text-blue-700 dark:text-blue-300' : 'text-blue-600 dark:text-blue-400 hover:text-blue-700'}`}
      >
        <HelpCircle className="w-4 h-4" />
      </span>
      {offen && createPortal(
        <div
          ref={blaseRef}
          style={{ position: 'fixed', top: pos?.top ?? -9999, left: pos?.left ?? -9999, width: BREITE, visibility: pos ? 'visible' : 'hidden' }}
          className="z-[60] max-h-[70vh] overflow-y-auto rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 shadow-xl p-4"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-start gap-2 mb-1.5">
            {titel && <h4 className="flex-1 text-xs font-bold text-slate-900 dark:text-white">{titel}</h4>}
            <button
              onClick={(e) => { e.stopPropagation(); setOffen(false); }}
              aria-label="Schließen"
              className="ml-auto shrink-0 p-0.5 rounded text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-700"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
          <p className="text-xs leading-relaxed text-slate-600 dark:text-slate-300 whitespace-pre-line">{text}</p>
        </div>,
        document.body,
      )}
    </>
  );
}
